import { useMemo } from 'react';

import { camelCase, lowerCase, sortBy, uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import { GroupBy, Quantities } from '../../constants';

import { sortAlphabeticallyBy } from 'utils/misc';

import {
  compareWithAtom,
  groupByAtom,
  quantitiesAtom,
  selectedCategoriesAtom,
  selectedFunctionsAtom
} from '../../atoms';

import { Box, Grid, Stack, TextField, Typography } from '@mui/material';

import { MultiSelect, Select } from 'components/shared';

export default function Settings() {
  const { t } = useTranslation('common');
  const unknown = t('unknown');

  const [quantities] = useRecoilState(quantitiesAtom);
  const [groupBy, setGroupBy] = useRecoilState(groupByAtom);
  const [selectedCategories, setSelectedCategories] = useRecoilState(
    selectedCategoriesAtom
  );
  const [selectedFunctions, setSelectedFunctions] = useRecoilState(
    selectedFunctionsAtom
  );
  const [compareWith, setCompareWith] = useRecoilState(compareWithAtom);

  const categoryOptions = useMemo(() => {
    const opts = quantities
      .flatMap((d) => d.data)
      .map(({ id, category }) => ({
        id,
        name: category || unknown,
        label: category
          ? t(`dhtCategories.${camelCase(lowerCase(category))}`)
          : unknown
      }));

    return uniqBy(
      sortBy(sortAlphabeticallyBy(opts, 'name'), (o) => o.name === unknown),
      'label'
    );
  }, [quantities]);

  const functionOptions = useMemo(() => {
    const opts = uniqBy(
      quantities.flatMap((d) => d.data),
      'functionDescription'
    ).map(({ id, functionDescription }) => ({
      id,
      name: functionDescription || unknown
    }));

    return sortBy(
      sortAlphabeticallyBy(opts, 'name'),
      (o) => o.name === unknown
    );
  }, [quantities]);

  const onCompareWithSelected = (
    option:
      | Quantities.WORK_DEMAND
      | Quantities.BASE_PLAN
      | Quantities.ROSTER
      | ''
  ) => {
    setCompareWith(option);

    if (option) {
      setGroupBy('');
    }
  };

  const onGroupBySelected = (option: GroupBy.CATEGORY | GroupBy.FUNCTION) => {
    setCompareWith('');
    setGroupBy(option);
  };

  return (
    <Box className="settings">
      <Grid container spacing={3}>
        <Grid item xs={6} minWidth={250}>
          <Typography variant="h3" mb={3}>
            {t('filter')}
          </Typography>
          <Stack spacing={3}>
            <MultiSelect
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('selectCategories')}
                  size="small"
                />
              )}
              options={categoryOptions}
              selectedOptions={selectedCategories}
              onChange={(options) => {
                setSelectedCategories(options);
              }}
              optionLabelProp="label"
              disableSorting
              renderTags={(e) => `${e.length} ${t('selected').toLowerCase()}`}
              disableClearable={false}
            />
            <MultiSelect
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('selectFunctions')}
                  size="small"
                />
              )}
              options={functionOptions}
              selectedOptions={selectedFunctions}
              onChange={(options) => {
                setSelectedFunctions(options);
              }}
              disableSorting
              renderTags={(e) => `${e.length} ${t('selected').toLowerCase()}`}
              disableClearable={false}
            />
          </Stack>
        </Grid>
        <Grid item xs={6} minWidth={250}>
          <Typography variant="h3" mb={3}>
            {t('view')}
          </Typography>
          <Stack spacing={3} sx={{ marginTop: '-3px' }}>
            <Select
              label={t('compareWith')}
              valueProp={compareWith}
              options={[
                {
                  value: Quantities.WORK_DEMAND,
                  label: t('analyzer.filters.workDemand')
                },
                {
                  value: Quantities.BASE_PLAN,
                  label: t('analyzer.filters.basePlan')
                },
                {
                  value: Quantities.ROSTER,
                  label: t('analyzer.filters.roster')
                }
              ]}
              onSelected={onCompareWithSelected}
              className="compare-with group-row"
              hasClearButton
            />
            <Select
              label={t('groupBy')}
              valueProp={groupBy}
              options={[
                {
                  value: GroupBy.CATEGORY,
                  label: t('category')
                },
                {
                  value: GroupBy.FUNCTION,
                  label: t('function')
                }
              ]}
              onSelected={onGroupBySelected}
              hasClearButton
            />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
